import { Daycare, DaycareType } from '@/types/Daycare.ts'
import { FunctionComponent, JSX, useEffect, useState } from 'react'
import styles from '../meal.module.scss'
import Title from '@/components/_custom/Title/title.tsx'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { MealAccessibilitySection } from '@/types/Meal.ts'
import { GET_DAYCARE_MEAL_ACCESSIBILITY_SECTION } from '@/modules/API/meal/getMealDaycareSettings.ts'
import Loading from '@/components/Loading/Loading.tsx'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage.tsx'
import { useLazyQuery } from '@apollo/client'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import MealAccessibilityRow from './mealAccessibilityRow.tsx'
import AddMealAccessibilityDialog from './Dialog/AddMealAccessibilityDialog.tsx'
import moment from 'moment-timezone'

interface MealAccessibilitiesProps {
    daycare: Daycare
    canUpdate: boolean
}

const MealAccessibilities: FunctionComponent<MealAccessibilitiesProps> = ({
    daycare,
    canUpdate
}: MealAccessibilitiesProps) => {
    const { t } = useTranslation()

    const [addMealAccessibilityDialog, setAddMealAccessibilityDialog] = useState<boolean>(false)

    const [accessibilities, setAccessibilities] = useState<MealAccessibilitySection[]>([])

    const [getMealAccessibility, { loading, data, error, refetch }] = useLazyQuery(
        GET_DAYCARE_MEAL_ACCESSIBILITY_SECTION,
        {
            variables: {
                daycareId: daycare.id
            }
        }
    )

    useEffect(() => {
        if (data) {
            setAccessibilities(data.getDaycareMealAccessibility)
        }
    }, [data])

    useEffect(() => {
        getMealAccessibility()
    }, [daycare.id])

    const isRPEOrLAEP: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    return (
        <div className={styles.BlockPlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myMealHours')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setAddMealAccessibilityDialog(true)}
                        />
                    )}
                </div>
                {loading && <Loading overall={false} />}
                {error && <ErrorMessage error={t('generalError')} />}
                <TableContainer className={styles.TablePlacement}>
                    <Table className={styles.Table}>
                        <TableHead className={styles.TableHeader}>
                            <TableRow>
                                <TableCell sx={{ width: '30%' }}>{t(isRPEOrLAEP ? 'sector' : 'section')}</TableCell>
                                <TableCell sx={{ width: '25%' }}>{t('hour')}</TableCell>
                                <TableCell sx={{ width: '25%' }}>{t('mealType')}</TableCell>
                                {canUpdate && (
                                    <TableCell colSpan={2} sx={{ width: '20%' }}>
                                        {t('actions')}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accessibilities.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={canUpdate ? 5 : 3}>{t('noMealAccessibilities')}</TableCell>
                                </TableRow>
                            ) : (
                                [...accessibilities]
                                    .sort((a: MealAccessibilitySection, b: MealAccessibilitySection): number => {
                                        const diff: number = moment(a.start, 'HH:mm:ss').diff(
                                            moment(b.start, 'HH:mm:ss')
                                        )
                                        if (diff !== 0) return diff
                                        const typeDiff: number = a.type.localeCompare(b.type)
                                        return typeDiff === 0 ? a.sectionName.localeCompare(b.sectionName) : typeDiff
                                    })
                                    .map(
                                        (accessibility: MealAccessibilitySection): JSX.Element => (
                                            <MealAccessibilityRow
                                                key={
                                                    accessibility.id +
                                                    accessibility.sectionId +
                                                    moment(accessibility.start).format('HH:mm')
                                                }
                                                accessibility={accessibility}
                                                daycare={daycare}
                                                canUpdate={canUpdate}
                                                refetch={refetch}
                                                isRPEOrLAEP={isRPEOrLAEP}
                                            />
                                        )
                                    )
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <AddMealAccessibilityDialog
                open={addMealAccessibilityDialog}
                handleClose={() => setAddMealAccessibilityDialog(false)}
                refetch={refetch}
                daycare={daycare}
                isRPEOrLAEP={isRPEOrLAEP}
            />
        </div>
    )
}

export default MealAccessibilities
